// Pure state-reducer helpers for the System Breakdown Structure (SBS).
//
// Each function takes the current `estimate` state and returns a new state
// (immutably).  The EstimateContext wraps these with `setEstimate(...)`.
// Keeping them stateless allows direct unit testing and shrinks the context.

import {
  buildEmptySystem,
  buildEmptySubsystem,
  buildEmptySubsubsystem,
  buildDefaultSbsPlan,
  computeSbsCounts,
} from "../lib/s1000dData";

const coerceField = (field, value) =>
  field === "name" ? value : (value === "" ? 0 : Number(value) || 0);

const _systems = (e) => e.sbsPlan?.systems || [];

const _withSystems = (e, systems) => ({ ...e, sbsPlan: { ...e.sbsPlan, systems } });

export const sbsAddSystem = (e) =>
  _withSystems(e, [..._systems(e), buildEmptySystem()]);

export const sbsRemoveSystem = (e, sysId) =>
  _withSystems(e, _systems(e).filter((s) => s.id !== sysId));

export const sbsUpdateSystem = (e, sysId, field, value) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : { ...s, [field]: coerceField(field, value) }
  ));

export const sbsAddSubsystem = (e, sysId) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : { ...s, subsystems: [...(s.subsystems || []), buildEmptySubsystem()] }
  ));

export const sbsRemoveSubsystem = (e, sysId, subId) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : { ...s, subsystems: (s.subsystems || []).filter((sub) => sub.id !== subId) }
  ));

export const sbsUpdateSubsystem = (e, sysId, subId, field, value) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : {
      ...s,
      subsystems: (s.subsystems || []).map((sub) =>
        sub.id !== subId ? sub : { ...sub, [field]: coerceField(field, value) }
      ),
    }
  ));

export const sbsAddSubsubsystem = (e, sysId, subId) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : {
      ...s,
      subsystems: (s.subsystems || []).map((sub) =>
        sub.id !== subId ? sub : { ...sub, subsubsystems: [...(sub.subsubsystems || []), buildEmptySubsubsystem()] }
      ),
    }
  ));

export const sbsRemoveSubsubsystem = (e, sysId, subId, ssId) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : {
      ...s,
      subsystems: (s.subsystems || []).map((sub) =>
        sub.id !== subId ? sub : { ...sub, subsubsystems: (sub.subsubsystems || []).filter((ss) => ss.id !== ssId) }
      ),
    }
  ));

export const sbsUpdateSubsubsystem = (e, sysId, subId, ssId, field, value) =>
  _withSystems(e, _systems(e).map((s) =>
    s.id !== sysId ? s : {
      ...s,
      subsystems: (s.subsystems || []).map((sub) =>
        sub.id !== subId ? sub : {
          ...sub,
          subsubsystems: (sub.subsubsystems || []).map((ss) =>
            ss.id !== ssId ? ss : { ...ss, [field]: coerceField(field, value) }
          ),
        }
      ),
    }
  ));

export const sbsUpdateField = (e, field, value) => ({
  ...e,
  sbsPlan: { ...e.sbsPlan, [field]: value },
});

export const sbsUpdateHoursPerItem = (e, level, value) => ({
  ...e,
  sbsPlan: {
    ...e.sbsPlan,
    hoursPerItem: {
      ...(e.sbsPlan?.hoursPerItem || { simple: 6, intermediate: 8, complex: 12 }),
      [level]: value === "" ? 0 : Number(value) || 0,
    },
  },
});

// Merge an imported SBS payload into an estimate state. Replace mode wipes
// systems and inserts the imported tree; Merge accumulates quantities into
// matching system/subsystem/subsubsystem names (case-insensitive).
export const sbsApplyImported = (e, parsed, mode = "replace") => {
  const baseSystems = mode === "merge" ? (e.sbsPlan?.systems || []) : [];
  const byName = new Map();
  baseSystems.forEach((s) => byName.set(String(s.name || "").toLowerCase(), s));
  const merged = mode === "merge" ? [...baseSystems] : [];
  for (const incomingSys of (parsed.systems || [])) {
    const key = String(incomingSys.name || "").toLowerCase();
    const existing = mode === "merge" ? byName.get(key) : null;
    if (existing) {
      existing.generalQty += incomingSys.generalQty;
      existing.repairableQty += incomingSys.repairableQty;
      const subByName = new Map((existing.subsystems || []).map((s) => [String(s.name || "").toLowerCase(), s]));
      for (const incSub of (incomingSys.subsystems || [])) {
        const subKey = String(incSub.name || "").toLowerCase();
        const existingSub = subByName.get(subKey);
        if (existingSub) {
          existingSub.generalQty += incSub.generalQty;
          existingSub.repairableQty += incSub.repairableQty;
          const ssByName = new Map((existingSub.subsubsystems || []).map((s) => [String(s.name || "").toLowerCase(), s]));
          for (const incSs of (incSub.subsubsystems || [])) {
            const ssKey = String(incSs.name || "").toLowerCase();
            const existingSs = ssByName.get(ssKey);
            if (existingSs) {
              existingSs.generalQty += incSs.generalQty;
              existingSs.repairableQty += incSs.repairableQty;
            } else {
              existingSub.subsubsystems = [...(existingSub.subsubsystems || []), incSs];
              ssByName.set(ssKey, incSs);
            }
          }
        } else {
          existing.subsystems = [...(existing.subsystems || []), incSub];
          subByName.set(subKey, incSub);
        }
      }
    } else {
      merged.push(incomingSys);
      byName.set(key, incomingSys);
    }
  }
  return { ...e, sbsPlan: { ...e.sbsPlan, systems: merged } };
};

// Push SBS counts into the DM matrix.  Replaces or adds (merge) the qty
// cells for generalDmType and repairableDmType across simple / intermediate /
// complex.  Also writes hoursPerItem into the matching hours cells when
// either type is the active target.
export const sbsApplyToDmMatrix = (e, mode = "replace") => {
  const sbs = e.sbsPlan || buildDefaultSbsPlan();
  const counts = computeSbsCounts(sbs);
  const genCode = sbs.generalDmType || "040";
  const repCode = sbs.repairableDmType || "520";
  const hpi = sbs.hoursPerItem || { simple: 6, intermediate: 8, complex: 12 };
  const next = {};
  for (const [code, row] of Object.entries(e.dmMatrix || {})) {
    next[code] = {
      simple: { ...row.simple },
      intermediate: { ...row.intermediate },
      complex: { ...row.complex },
    };
  }
  const ensureRow = (code) => {
    if (!next[code]) {
      next[code] = {
        simple: { qty: 0, hours: Number(hpi.simple) || 6 },
        intermediate: { qty: 0, hours: Number(hpi.intermediate) || 8 },
        complex: { qty: 0, hours: Number(hpi.complex) || 12 },
      };
    }
  };
  ensureRow(genCode); ensureRow(repCode);
  const writeCell = (code, level, qty) => {
    const prev = Number(next[code][level].qty) || 0;
    const newQty = mode === "merge" ? prev + qty : qty;
    next[code][level] = {
      qty: newQty,
      hours: Number(hpi[level]) || Number(next[code][level].hours) || 0,
    };
  };
  writeCell(genCode, "simple", counts.simple.general);
  writeCell(genCode, "intermediate", counts.intermediate.general);
  writeCell(genCode, "complex", counts.complex.general);
  writeCell(repCode, "simple", counts.simple.repairable);
  writeCell(repCode, "intermediate", counts.intermediate.repairable);
  writeCell(repCode, "complex", counts.complex.repairable);
  return { ...e, dmMatrix: next };
};
